import * as Lambda from '../../services/LambdaApi';
import { Post, PostListResponse } from '../types/Post';

const listPosts = async ({
    facilityId,
    callerResidentId,
    ResidentIds,
    StaffIds,
    GroupIds,
    pageNo = 1,
    pageSize = 20,
}: {
    facilityId: string;
    callerResidentId: string;
    ResidentIds?: string[];
    StaffIds?: string[];
    GroupIds?: string[];
    pageNo?: number;
    pageSize?: number;
}): Promise<PostListResponse> => {
    const apiPath = `/s2l/${facilityId}/listPosts`;
    const filter: {
        callerType: 'resident';
        callerResidentId: string;
        ResidentIds?: string[];
        StaffIds?: string[];
        GroupIds?: string[];
    } = {
        callerType: 'resident',
        callerResidentId,
    };
    if (ResidentIds?.length) {
        filter.ResidentIds = ResidentIds;
    }
    if (StaffIds?.length) {
        filter.StaffIds = StaffIds;
    }
    if (GroupIds?.length) {
        filter.GroupIds = GroupIds;
    }
    const params = {
        Filter: filter,
        pagingSorting: {
            page_no: pageNo,
            page_size: pageSize,
        },
    };
    const response = await Lambda.Post(apiPath, params, 'lambda');
    if (!response?.Result?.Content) {
        throw new Error('Error fetching posts');
    }
    return response.Result.Content as PostListResponse;
};

const getPost = async ({
    facilityId,
    postId,
    callerResidentId,
}: {
    facilityId: string;
    postId: string;
    callerResidentId: string;
}): Promise<Post> => {
    const apiPath = `/s2l/${facilityId}/getPost`;
    const params = {
        _id: postId,
        callerType: 'resident',
        callerResidentId,
    };
    const response = await Lambda.Post(apiPath, params, 'lambda');
    if (!response?.Result?.Content) {
        throw new Error('Error fetching post');
    }
    return response.Result.Content as Post;
};

const getPostUploadUrl = async ({
    facilityId,
    fileName,
}: {
    facilityId: string;
    fileName: string;
}): Promise<{ presignedUrl: string; imageUrl: string; fileName: string }> => {
    const apiPath = `/s2l/${facilityId}/getPostUploadUrl`;
    const params = { fileName };
    const response = await Lambda.Post(apiPath, params, 'lambda');
    if (!response?.Result?.Content) {
        throw new Error('Error getting upload URL');
    }
    return response.Result.Content;
};

const createPost = async ({
    facilityId,
    registrantId,
    content,
    imageFileName,
    imageUrl,
    groups,
}: {
    facilityId: string;
    registrantId: string;
    content: string;
    imageFileName?: string;
    imageUrl?: string;
    groups?: string[];
}) => {
    const apiPath = `/s2l/${facilityId}/createPost`;
    const params: {
        Content: string;
        AuthorType: 'resident';
        RegistrantId: string;
        ImageFileName?: string;
        ImageUrl?: string;
        Groups?: string[];
    } = {
        Content: content,
        AuthorType: 'resident',
        RegistrantId: registrantId,
    };
    if (imageFileName) {
        params.ImageFileName = imageFileName;
        params.ImageUrl = imageUrl;
    }
    if (groups) {
        params.Groups = groups;
    }
    const response = await Lambda.Post(apiPath, params, 'lambda');
    if (!response?.Result?.Content && !response?.Result?.Success) {
        throw new Error(response?.Result?.Error || 'Error creating post');
    }
    return response.Result.Content;
};

const updatePost = async ({
    facilityId,
    registrantId,
    postId,
    content,
    imageFileName,
    imageUrl,
    groups,
}: {
    facilityId: string;
    registrantId: string;
    postId: string;
    content: string;
    imageFileName?: string;
    imageUrl?: string;
    groups?: string[];
}) => {
    const apiPath = `/s2l/${facilityId}/updatePost`;
    const params: {
        _id: string;
        AuthorType: 'resident';
        RegistrantId: string;
        Content: string;
        ImageFileName?: string;
        ImageUrl?: string;
        Groups?: string[];
    } = {
        _id: postId,
        AuthorType: 'resident',
        RegistrantId: registrantId,
        Content: content,
    };
    if (imageFileName !== undefined) {
        params.ImageFileName = imageFileName;
        params.ImageUrl = imageUrl || '';
    }
    if (groups !== undefined) {
        params.Groups = groups;
    }
    const response = await Lambda.Post(apiPath, params, 'lambda');
    if (!response?.Result?.Content && !response?.Result?.Success) {
        throw new Error(response?.Result?.Error || 'Error updating post');
    }
    return response.Result.Content;
};

const deletePost = async ({
    facilityId,
    registrantId,
    postId,
}: {
    facilityId: string;
    registrantId: string;
    postId: string;
}) => {
    const apiPath = `/s2l/${facilityId}/deletePost`;
    const params = {
        _id: postId,
        AuthorType: 'resident',
        RegistrantId: registrantId,
    };
    const response = await Lambda.Post(apiPath, params, 'lambda');
    if (!response?.Result?.Success) {
        throw new Error(response?.Result?.Error || 'Error deleting post');
    }
    return response.Result.Content;
};

const likePost = async ({
    facilityId,
    postId,
    registrantId,
}: {
    facilityId: string;
    postId: string;
    registrantId: string;
}): Promise<{ liked: boolean; likeCount: number }> => {
    const apiPath = `/s2l/${facilityId}/likePost`;
    const params = {
        PostId: postId,
        AuthorType: 'resident',
        RegistrantId: registrantId,
    };
    const response = await Lambda.Post(apiPath, params, 'lambda');
    if (!response?.Result?.Content) {
        throw new Error(response?.Result?.Error || 'Error liking post');
    }
    return response.Result.Content as { liked: boolean; likeCount: number };
};

const checkHasPosted = async ({
    facilityId,
    callerResidentId,
}: {
    facilityId: string;
    callerResidentId: string;
}): Promise<boolean> => {
    const apiPath = `/s2l/${facilityId}/listPosts`;
    const params = {
        Filter: {
            callerType: 'resident',
            callerResidentId,
            AuthorId: callerResidentId,
            AuthorType: 'resident',
        },
        pagingSorting: {
            page_no: 1,
            page_size: 1,
        },
    };
    const response = await Lambda.Post(apiPath, params, 'lambda');
    if (!response?.Result?.Content) {
        return false;
    }
    const data = response.Result.Content as PostListResponse;
    return (data.TotRecords || 0) > 0;
};

const reportContent = async ({
    facilityId,
    contentType,
    contentId,
    reason,
    registrantId,
}: {
    facilityId: string;
    contentType: 'Post' | 'Comment';
    contentId: string;
    reason?: string;
    registrantId: string;
}): Promise<{ ok: boolean; alreadyReported?: boolean }> => {
    const apiPath = `/s2l/${facilityId}/reportContent`;
    const params = {
        ContentType: contentType,
        ContentId: contentId,
        ...(reason ? { Reason: reason } : {}),
        registrantId,
    };
    const response = await Lambda.Post(apiPath, params, 'lambda');
    if (!response?.Result?.Content && !response?.Result?.Success) {
        throw new Error(response?.Result?.Error || 'Error reporting content');
    }
    return response?.Result?.Content ?? { ok: true };
};

export { listPosts, getPost, getPostUploadUrl, createPost, updatePost, deletePost, likePost, checkHasPosted, reportContent };
